import { Component, OnDestroy, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { LazyLoadEvent } from 'primeng/api';
import { BaseComponent } from 'src/app/shared/base.component';
import { UserService } from 'src/app/services/user.service';
import { WebStorageService } from 'src/app/services/web-storage.service';
import { environment } from 'src/environments/environment';
import { PagedResult, ResultOfT } from 'src/app/models/response/result';
import { UserInfoResponseModel } from 'src/app/models/response/userInfo-response';
import { CustomerSubscriptionResponseModel } from 'src/app/models/response/customer-subscription-response';
import {
  CustomerSubscriptionRequestModel,
  GetCustomerSubscriptionsRequestModel
} from 'src/app/models/request/customer-subscription-request';
import { CompanyResponseModel } from 'src/app/models/response/company-response';

@Component({
  selector: 'app-all-subscriptions',
  templateUrl: './all-subscriptions.component.html',
  styleUrls: ['./all-subscriptions.component.sass']
})
export class AllSubscriptionsComponent extends BaseComponent implements OnInit, OnDestroy {
  private unsubscriber = new Subject<boolean>();
  private searchTimer: any;

  Menustatus: boolean = false;
  filterResposniveEvent: boolean = false;
  isCustomerAdmin: boolean = false;

  // Messages / loader
  displayMessage: boolean = false;
  messageClass: boolean = false;
  message: string = '';
  loading: boolean = false;

  // Filters
  customerOptions: { label: string; value: number }[] = [];
  selectedCustomerId: number | null = null;
  selectedStatus: string | null = null;
  searchText: string = '';
  subscriptionTypeOptions = [
    { label: 'Base', value: 'Base' },
    { label: 'Pro', value: 'Pro' },
    { label: 'Elite', value: 'Elite' }
  ];
  showClearFilter: boolean = false;
  displayClearButton: boolean = false;

  // Grid + pagination (same pattern as customer-log)
  subscriptions: CustomerSubscriptionResponseModel[] = [];
  pageNumber: number = 1;
  pageSize: number = 50;
  totalRecords: number = 0;
  isLazyLoadingEnabled: boolean = false;
  sortField: string;
  sortOrder: number = -1;

  // New / edit subscription dialog
  displayNewSubscriptionDialog: boolean = false;
  newSubscription: CustomerSubscriptionRequestModel = new CustomerSubscriptionRequestModel();
  startDateValue: Date | null = null;
  endDateValue: Date | null = null;
  minStartDate: Date | null = null;
  blockedUntil: Date | null = null;
  blockedRanges: { start: Date; end: Date }[] = [];
  disabledDates: Date[] = [];

  statusList = [
    { label: 'Active', value: 'Active' },
    { label: 'Upcoming', value: 'Upcoming' },
    { label: 'Expired', value: 'Expired' }
  ];

  constructor(private userService: UserService, private router: Router,
    private webStorageService: WebStorageService) {
    super();
  }

  goToCustomerSubscriptions(subscription: CustomerSubscriptionResponseModel): void {
    if (!subscription?.CompanyId) { return; }
    this.router.navigate(['/company/subscription'], {
      queryParams: {
        customerId: subscription.CompanyId,
        name: subscription.FullName || this.getCustomerName(subscription.CompanyId),
        subscriptionId: subscription.SubscriptionId
      }
    });
  }

  ngOnInit(): void {
    this.getAllCustomerAdmins();
    setTimeout(() => {
      this.triggerInitialLoad();
    }, 100);
  }

  // ────────────────────────────────────────
  // Customer admins (filter + dialog dropdown)
  // ────────────────────────────────────────
  getAllCustomerAdmins(): void {
    this.loading = true;
    this.userService.GetAllCompanyAdmins()
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((result: ResultOfT<CompanyResponseModel[]>) => {
        this.processResult<any>(result, () => {
          if (result.IsSuccess) {
            this.customerOptions = (result.ResponseModel || []).map((c: CompanyResponseModel) => ({
              label: c.CompanyName,
              value: c.CompanyId
            }));
          } else {
            this.timeoutFunct(result.Message, false);
          }
        });
      }, (httpResponseError) => {
        this.loading = false;
        this.timeoutFunct(httpResponseError.message, false);
      });
  }

  // ────────────────────────────────────────
  // Subscriptions list with lazy pagination
  // ────────────────────────────────────────
  triggerInitialLoad(): void {
    this.onLazyLoad(this.buildFirstPageEvent());
  }

  private buildFirstPageEvent(): LazyLoadEvent {
    return {
      first: 0,
      rows: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder
    } as LazyLoadEvent;
  }

    onLazyLoad(event: LazyLoadEvent): void {
    if (!this.subscriptions) {
      this.subscriptions = [];
    }

    const incomingField = event.sortField ? event.sortField.toString() : '';
    const incomingOrder = event.sortOrder === 1 ? 1 : -1;
    const sortChanged = incomingField !== (this.sortField || '')
      || (incomingField !== '' && incomingOrder !== this.sortOrder);

    if (sortChanged || !event.first) {
      // First page / filter / sort change → reset
      this.subscriptions = [];
      this.pageNumber = 1;
      this.totalRecords = 0;
    } else {
      // Scrolled → load next page
      const totalLoadedRecords = this.subscriptions.length || 0;
      if (totalLoadedRecords >= this.totalRecords && this.subscriptions.length > 0) {
        return;
      }
      this.pageNumber = Math.floor(totalLoadedRecords / this.pageSize) + 1;
    }
    this.loading = true;

    const sortOrderString = incomingOrder === 1 ? 'asc' : 'desc';

    const requestModel = new GetCustomerSubscriptionsRequestModel();
    requestModel.PageNumber = this.pageNumber;
    requestModel.PageSize = this.pageSize;
    requestModel.CustomerName = this.searchText || '';
    requestModel.SortColumn = incomingField || 'DateTimeCreated';
    requestModel.SortDirection = sortOrderString;
    requestModel.CustomerId = this.selectedCustomerId || undefined;
    requestModel.Status = this.selectedStatus || '';

    this.sortField = incomingField;
    this.sortOrder = incomingOrder;

    this.userService.getCustomerSubscriptions(requestModel)
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((result: ResultOfT<PagedResult<CustomerSubscriptionResponseModel>>) => {
        this.processResult<any>(result, () => {
          if (result.IsSuccess) {
            if (result.ResponseModel && result.ResponseModel.Data) {
              this.totalRecords = result.ResponseModel.TotalRecords || 0;
              const newRecords = result.ResponseModel.Data;
              this.subscriptions = this.pageNumber === 1
                ? [...newRecords]
                : [...this.subscriptions, ...newRecords];
              this.isLazyLoadingEnabled = true;
              // Everything is loaded → sort locally so every column works asc/desc
              if (this.subscriptions.length >= this.totalRecords) {
                this.sortLocally();
              }
            }
          } else {
            this.timeoutFunct(result.Message, false);
          }
        });
      }, (httpResponseError) => {
        this.loading = false;
        this.timeoutFunct(httpResponseError.message, false);
      });
  }

  private sortLocally(): void {
    if (!this.sortField) { return; }
    const field = this.sortField === 'FullName' ? 'FullName' : this.sortField;
    const dir = this.sortOrder;
    const dateFields = ['StartDate', 'EndDate', 'DateTimeCreated'];
    const numberFields = ['SubscriptionVolume', 'ConsumedVolume', 'RemainingVolume', 'UsagePercent', 'SubscriptionId'];

    const val = (s: any) => {
      const v = s[field];
      if (v === null || v === undefined || v === '') { return null; }
      if (dateFields.indexOf(field) >= 0) { return new Date(v).getTime(); }
      if (numberFields.indexOf(field) >= 0) {
        const n = Number(String(v).replace(/,/g, ''));
        return isNaN(n) ? null : n;
      }
      return typeof v === 'string' ? v.toLowerCase() : v;
    };

    this.subscriptions = [...this.subscriptions].sort((a, b) => {
      const x = val(a), y = val(b);
      if (x === y) { return 0; }
      if (x === null) { return 1; }
      if (y === null) { return -1; }
      return (x < y ? -1 : 1) * dir;
    });
  }

  onSearchChange(searchText: string): void {
    this.searchText = searchText;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.onLazyLoad(this.buildFirstPageEvent()), 400);
  }

  applyFilters(): void {
    this.showClearFilter = true;
    this.displayClearButton = true;
    this.filterResposniveEvent = false;
    this.onLazyLoad(this.buildFirstPageEvent());
  }

  resetFilter(): void {
    this.selectedCustomerId = null;
    this.selectedStatus = null;
    this.searchText = '';
    this.showClearFilter = false;
    this.displayClearButton = false;
    this.filterResposniveEvent = false;
    this.onLazyLoad(this.buildFirstPageEvent());
  }

  // ────────────────────────────────────────
  // Create / update subscription
  // ────────────────────────────────────────
  openNewSubscriptionDialog(subscription?: CustomerSubscriptionResponseModel): void {
    const model = new CustomerSubscriptionRequestModel();
    if (subscription) {
      model.SubscriptionId = subscription.SubscriptionId;
      model.CustomerId = subscription.CompanyId;
      model.StartDate = this.toInputDate(subscription.StartDate);
      model.EndDate = this.toInputDate(subscription.EndDate);
      model.Status = subscription.Status;
      model.SubscriptionVolume = subscription.SubscriptionVolume;
      model.SubscriptionType = subscription.SubscriptionType;
      model.SubscriptionContactName = subscription.SubscriptionContactName;
      model.SubscriptionContactEmail = subscription.SubscriptionContactEmail;
    }
    this.newSubscription = model;
    this.startDateValue = subscription?.StartDate ? new Date(subscription.StartDate) : null;
    this.endDateValue = subscription?.EndDate ? new Date(subscription.EndDate) : null;
    this.minStartDate = null;
    this.blockedUntil = null;
    this.blockedRanges = [];
    this.disabledDates = [];
    if (model.CustomerId) {
      this.loadCustomerDateRestriction(model.CustomerId);
    }
    this.displayNewSubscriptionDialog = true;
  }

  onDialogCustomerChange(customerId: number): void {
    this.minStartDate = null;
    this.blockedUntil = null;
    this.blockedRanges = [];
    this.disabledDates = [];
    if (customerId) {
      this.loadCustomerDateRestriction(customerId);
    }
  }

  /**
   * Finds the latest end date among the customer's Active/Upcoming subscriptions
   * (excluding the one being edited). New start date must be after that date.
   */
  private loadCustomerDateRestriction(customerId: number): void {
    const req = new GetCustomerSubscriptionsRequestModel();
    req.CustomerId = customerId;
    req.PageNumber = 1;
    req.PageSize = 1000;

    this.loading = true;
    this.userService.getCustomerSubscriptions(req)
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((result: ResultOfT<PagedResult<CustomerSubscriptionResponseModel>>) => {
        const data = (result?.ResponseModel?.Data || [])
          .filter(s => String(s.CompanyId) === String(customerId))
          .filter(s => s.SubscriptionId !== this.newSubscription.SubscriptionId);

        const today = this.startOfDay(new Date());

        // Only ranges that are still running or in the future block dates
        this.blockedRanges = data
          .filter(s => s.StartDate && s.EndDate)
          .map(s => ({ start: this.startOfDay(new Date(s.StartDate)), end: this.startOfDay(new Date(s.EndDate)) }))
          .filter(r => r.end >= today)
          .sort((a, b) => a.start.getTime() - b.start.getTime());

        this.disabledDates = this.buildDisabledDates();
        // Old single "blocked until" rule no longer applies
        this.blockedUntil = null;

        // New subscription: clear dates that fall inside an existing range
        if (!this.newSubscription?.SubscriptionId) {
          if (this.startDateValue && this.isDateBlocked(this.startDateValue)) {
            this.startDateValue = null;
          }
          if (this.endDateValue && this.isDateBlocked(this.endDateValue)) {
            this.endDateValue = null;
          }
        }
      }, (err) => {
        this.loading = false;
        this.timeoutFunct(err.message, false);
      });
  }

  /** True if the day falls inside any existing subscription range. */
  private isDateBlocked(day: Date): boolean {
    const d = this.startOfDay(day);
    return this.blockedRanges.some(r => d >= r.start && d <= r.end);
  }

  private buildDisabledDates(): Date[] {
    const dates: Date[] = [];
    this.blockedRanges.forEach(r => {
      for (let d = new Date(r.start); d <= r.end; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d));
      }
    });
    return dates;
  }

  private startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  /** Earliest selectable start: today for new subscriptions, unrestricted when editing. */
  get startMinDate(): Date | null {
    return this.newSubscription?.SubscriptionId ? null : this.startOfDay(new Date());
  }

  get endMinDate(): Date | null {
    return this.startDateValue || this.startMinDate;
  }

  /** Latest end: the day before the next existing subscription that starts after the chosen start. */
  get endMaxDate(): Date | null {
    if (!this.startDateValue) { return null; }
    const start = this.startOfDay(this.startDateValue);
    const next = this.blockedRanges.find(r => r.start > start);
    if (!next) { return null; }
    const max = new Date(next.start);
    max.setDate(max.getDate() - 1);
    return max;
  }

  onStartDateSelect(): void {
    if (this.endDateValue && this.startDateValue && this.endDateValue < this.startDateValue) {
      this.endDateValue = null;
    }
    const max = this.endMaxDate;
    if (this.endDateValue && max && this.startOfDay(this.endDateValue) > max) {
      this.endDateValue = null;
    }
  }

  closeNewSubscriptionDialog(): void {
    this.displayNewSubscriptionDialog = false;
    this.newSubscription = new CustomerSubscriptionRequestModel();
    this.startDateValue = null;
    this.endDateValue = null;
    this.minStartDate = null;
    this.blockedUntil = null;
    this.blockedRanges = [];
    this.disabledDates = [];
  }

  createSubscription(): void {
    const model = this.newSubscription;
    if (this.startDateValue && this.endDateValue) {
      const s = this.startOfDay(this.startDateValue);
      const e = this.startOfDay(this.endDateValue);
      if (this.blockedRanges.some(r => s <= r.end && e >= r.start)) {
        this.timeoutFunct('Subscription dates overlap an existing subscription for this customer.', false);
        return;
      }
    }
    model.StartDate = this.startDateValue ? this.toInputDate(this.startDateValue) : '';
    model.EndDate = this.endDateValue ? this.toInputDate(this.endDateValue) : '';
    const currentUser: any = this.webStorageService.getCurrentUserToken();
    model.CreatedBy = currentUser?.userId || 0;
    model.ModifiedBy = currentUser?.userId || 0;
    
    if (!model.CustomerId) {
      this.timeoutFunct('Please select a customer', false);
      return;
    }
    if (!model.StartDate || !model.EndDate) {
      this.timeoutFunct('Please select start and end date', false);
      return;
    }
    if (new Date(model.EndDate) < new Date(model.StartDate)) {
      this.timeoutFunct('End date must be after start date', false);
      return;
    }
    if (!model.SubscriptionType) {
      this.timeoutFunct('Please select a subscription type', false);
      return;
    }
    if (!model.SubscriptionVolume || Number(model.SubscriptionVolume) <= 0) {
      this.timeoutFunct('Please enter a valid subscription volume', false);
      return;
    }
    model.SubscriptionVolume = model.SubscriptionVolume.toString();

    this.loading = true;
    this.userService.createUpdateCustomerSubscription(model)
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((result: ResultOfT<any>) => {
        this.processResult<any>(result, () => {
          if (result.IsSuccess) {
            this.timeoutFunct(model.SubscriptionId ? 'Subscription updated' : 'Subscription created', true);
            this.closeNewSubscriptionDialog();
            this.onLazyLoad(this.buildFirstPageEvent());
          } else {
            this.timeoutFunct(result.Message || 'Error occured while saving subscription', false);
          }
        });
      }, (httpResponseError) => {
        this.loading = false;
        this.timeoutFunct(httpResponseError.message, false);
      });
  }

  getCustomerName(customerId: number): string {
    return this.customerOptions.find(c => c.value === customerId)?.label || '';
  }

  private toInputDate(value: Date | string): string {
    return value ? formatDate(value, 'yyyy-MM-dd', 'en-US') : '';
  }

  // ────────────────────────────────────────
  // UI helpers
  // ────────────────────────────────────────
  showMenu(): void {
    this.Menustatus = !this.Menustatus;
  }

  filterClickEvent(): void {
    this.filterResposniveEvent = !this.filterResposniveEvent;
  }

  timeoutFunct(msg: string, isSuccess: boolean): void {
    this.displayMessage = true;
    this.messageClass = isSuccess;
    this.message = msg;
    setTimeout(() => {
      this.message = '';
      this.displayMessage = false;
    }, environment.messageLifeSpan);
  }

  closeMessagePopup(): void {
    this.displayMessage = false;
    this.message = '';
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchTimer);
    if (this.unsubscriber) {
      this.unsubscriber.next(true);
      this.unsubscriber.complete();
    }
  }
}